import { useQuery } from '@apollo/client/react';
import { SubscriptionContent, SubscriptionGuide } from '../interfaces';
import { SubscriptionContentKind, SubscriptionProduct, SubscriptionSeries, subscriptionProductNames } from '../interfaces.enums';
import { GET_SUBSCRIPTION_GUIDES } from './subscriptions.graphql';
import { numberFormatted } from '../Formatters';

export const allSeries = [SubscriptionSeries.Planlegging, SubscriptionSeries.Byggdetaljer, SubscriptionSeries.Byggforvaltning];

export function useSubscriptionGuides() {
  const { data, loading, error } = useQuery<{ guides: SubscriptionGuide[] }>(GET_SUBSCRIPTION_GUIDES);
  return { guides: data?.guides, loading, error };
}

export function describeContent(content: SubscriptionContent | null | undefined, product: SubscriptionProduct | null | undefined): string {
  if (!content) return 'Ingen anvisninger';
  switch (content.kind) {
    case SubscriptionContentKind.Product:
      return product ? `Hele ${subscriptionProductNames[product]}` : 'Hele produktet';
    case SubscriptionContentKind.Series:
      return content.series.join(', ');
    default:
      return content.docIds.length === 1 ? '1 utvalgt anvisning' : `${content.docIds.length} utvalgte anvisninger`;
  }
}

export function currentGuidesGiven(
  content: Pick<SubscriptionContent, 'kind' | 'series' | 'docIds'> | null | undefined,
  product: SubscriptionProduct | null | undefined,
  guides: SubscriptionGuide[],
): SubscriptionGuide[] {
  if (!content || !product) return [];
  const docIds = new Set(content.docIds);
  return guides.filter(
    (g) =>
      g.isCurrent &&
      g.product === product &&
      (content.kind === SubscriptionContentKind.Product ||
        (content.kind === SubscriptionContentKind.Series && g.series != null && content.series.includes(g.series)) ||
        (content.kind === SubscriptionContentKind.Guides && docIds.has(g.docId))),
  );
}

export function guideLabel(guide: SubscriptionGuide): string {
  return `${guide.number} ${guide.title}${guide.isWithdrawn ? ' (utgått)' : guide.isCurrent ? '' : ' (planlagt)'}`;
}

export function withdrawnGuidesGiven(
  content: Pick<SubscriptionContent, 'kind' | 'docIds'>,
  product: SubscriptionProduct,
  guides: SubscriptionGuide[],
): SubscriptionGuide[] {
  const docIds = new Set(content.docIds);
  return guides.filter(
    (g) =>
      g.isWithdrawn &&
      g.product === product &&
      (content.kind === SubscriptionContentKind.Product || (content.kind === SubscriptionContentKind.Guides && docIds.has(g.docId))),
  );
}

/**
 * Withdrawn guides are not counted for series: the portal finds a withdrawn guide's series in BKS, where most have
 * none, while Fag places most of them in a series, so a count from Fag would promise more than the portal gives.
 */
export function describeGiven(
  content: Pick<SubscriptionContent, 'kind' | 'series' | 'docIds' | 'includesOldVersions'>,
  product: SubscriptionProduct,
  guides: SubscriptionGuide[],
): string {
  const current = `Gir ${numberFormatted(currentGuidesGiven(content, product, guides).length) ?? 0} gjeldende anvisninger`;
  if (!content.includesOldVersions) return `${current}.`;
  if (content.kind === SubscriptionContentKind.Series) return `${current} med tidligere versjoner, og utgåtte anvisninger i serien.`;
  const withdrawn = withdrawnGuidesGiven(content, product, guides).length;
  if (withdrawn === 0) return `${current} med tidligere versjoner.`;
  return `${current} med tidligere versjoner, og ${numberFormatted(withdrawn)} utgåtte ${withdrawn === 1 ? 'anvisning' : 'anvisninger'}.`;
}
